<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { whatsappLink, trackConversion } from '@/lib/whatsapp'
import QuoteButton from '@/components/QuoteButton.vue'
import WhatsappIcon from '@/components/WhatsappIcon.vue'

const props = defineProps({
  phone: { type: String, required: true }
})

// En la barra los dos canales conviven, pero el espacio no da para dos
// pastillas de texto en un teléfono: el formulario va como pastilla y WhatsApp
// como botón de ícono, que cabe en cualquier ancho y no repite "cotizar".
const whatsappHref = computed(() => whatsappLink(props.phone, 'cortinas a medida'))

// Over the hero the bar keeps a soft scrim rather than going fully
// transparent: the photo's ceiling is bright enough that the logo would drop
// to 3.9:1 against it.
const settled = ref(false)
const menuOpen = ref(false)

// The desktop layout only fits from lg up. At 768 the logo, the tagline, the
// nav links and the quote pill all try to share one bar and every one of them
// wraps, so tablets keep the hamburger.
//
// Y desde que son cinco los enlaces, la bajada "Cortinas a medida" espera hasta
// xl: en 1024 el logo no tiene dónde encogerse —va en una línea— y se metía
// debajo del primer enlace.
const links = [
  { href: '#productos', label: 'Cortinas' },
  { href: '#telas', label: 'Telas' },
  { href: '#servicios', label: 'Servicios' },
  { href: '#proceso', label: 'Cómo trabajamos' },
  { href: '#medir', label: 'Cómo medir' }
]

function onScroll () {
  settled.value = window.scrollY > 24
}

// The panel covers the viewport, so the page behind it must not scroll.
watch(menuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})

onMounted(() => {
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  document.body.style.overflow = ''
})
</script>

<template>
  <header
    class="fixed inset-x-0 top-0 z-50 transition-colors duration-500"
    :class="settled || menuOpen
      ? 'bg-night/90 backdrop-blur-md'
      : 'bg-linear-to-b from-night/65 to-transparent'"
  >
    <div class="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4 lg:px-10">
      <a href="#" class="-my-2 flex items-baseline gap-2.5 whitespace-nowrap py-2 text-linen">
        <img
          src="/lumina-isologo.png"
          alt=""
          class="h-8 w-auto self-center"
          width="182"
          height="160"
        >
        <span class="display text-xl sm:text-2xl">Casa Lumina</span>
        <span class="eyebrow hidden text-glow/90 xl:inline">Cortinas a medida</span>
      </a>

      <nav class="hidden items-center gap-8 lg:flex" aria-label="Secciones">
        <a
          v-for="link in links"
          :key="link.href"
          :href="link.href"
          class="whitespace-nowrap py-2 text-sm text-linen/80 transition-colors duration-300 hover:text-glow"
        >
          {{ link.label }}
        </a>
      </nav>

      <!-- Los dos canales de cotización, en el aire a cualquier ancho: el
           formulario como pastilla y WhatsApp como botón de ícono al lado. -->
      <div class="flex items-center gap-2">
        <QuoteButton size="small" label="Cotizar" source="header" collapse />

        <a
          :href="whatsappHref"
          target="_blank"
          rel="noopener"
          class="flex h-11 w-11 items-center justify-center rounded-full border border-linen/30 text-linen transition-colors duration-300 hover:border-glow hover:text-glow"
          aria-label="Cotizar por WhatsApp"
          @click="trackConversion('header', 'cortinas a medida')"
        >
          <WhatsappIcon class="h-5 w-5 shrink-0" />
        </a>

        <button
          type="button"
          class="flex h-11 w-11 items-center justify-center text-linen lg:hidden"
          :aria-expanded="menuOpen"
          aria-controls="mobile-menu"
          @click="menuOpen = !menuOpen"
        >
          <span class="sr-only">{{ menuOpen ? 'Cerrar menú' : 'Abrir menú' }}</span>
          <span class="relative block h-3.5 w-6">
            <span
              class="absolute left-0 block h-px w-full bg-current transition-all duration-300"
              :class="menuOpen ? 'top-1.5 rotate-45' : 'top-0'"
            ></span>
            <span
              class="absolute left-0 block h-px w-full bg-current transition-all duration-300"
              :class="menuOpen ? 'top-1.5 -rotate-45' : 'top-3'"
            ></span>
          </span>
        </button>
      </div>
    </div>

    <div
      v-show="menuOpen"
      id="mobile-menu"
      class="h-[calc(100dvh-4.75rem)] border-t border-linen/10 bg-night px-6 pb-6 pt-4 lg:hidden"
    >
      <nav class="flex flex-col" aria-label="Secciones">
        <a
          v-for="link in links"
          :key="link.href"
          :href="link.href"
          class="display border-b border-linen/10 py-4 text-2xl text-linen/85 transition-colors hover:text-glow"
          @click="menuOpen = false"
        >
          {{ link.label }}
        </a>
      </nav>
    </div>
  </header>
</template>
